
import React, { useState, useEffect, useMemo } from 'react';
import { ShiftInput, Shift, ShiftType, ExpenseItem, ExtraEarningsItem, ExpenseCategory, ExtraIncomeCategory, TransactionOrigin, UserProfile } from '../types';
import { supabase } from '../supabaseClient';
import { calculateEarnings } from '../services/calculationService';
import { Save, Calculator, Camera, X, PlusCircle, CalendarCheck, Calendar, Pencil, Trash2, CheckCircle2, Coins, Loader2, Check, Briefcase, Coffee, AlertTriangle, Wallet, ShoppingCart, TrendingUp, Bus, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { ImageUpload } from './ImageUpload';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';

interface Props {
    onSave: (shift: Shift | Shift[], navigateToDashboard?: boolean) => Promise<void> | void;
    initialData?: Partial<ShiftInput>;
    existingShifts?: Shift[];
    isPremiumOrVip?: boolean;
    onRequirePremium?: (featureName: string) => void;
    userGeneration?: 'OLD' | 'NEW';
    userProfile?: UserProfile | null;
    onProfileUpdate?: () => void;
}

export const ShiftEntry: React.FC<Props> = ({ onSave, initialData, existingShifts = [], isPremiumOrVip = false, onRequirePremium, userGeneration = 'OLD', userProfile, onProfileUpdate }) => {
    const [formData, setFormData] = useState<ShiftInput>(() => ({
        type: initialData?.type || 'WORK',
        date: initialData?.date || format(new Date(), 'yyyy-MM-dd'),
        startTime: initialData?.startTime || '06:00',
        endTime: initialData?.endTime || '18:00',
        isHoliday: initialData?.isHoliday || false,
        hasBilingual: initialData?.hasBilingual || false,
        bilingualStart: initialData?.bilingualStart || '19:00',
        bilingualEnd: initialData?.bilingualEnd || '22:00',
        appTips: initialData?.appTips || '',
        tips: initialData?.tips || '',
        expenses: initialData?.expenses || [],
        extraEarnings: initialData?.extraEarnings || [],
        unpaidBreakMinutes: initialData?.unpaidBreakMinutes || '',
        vehiclePlate: initialData?.vehiclePlate || '',
        carReadyOnTime: initialData?.carReadyOnTime ?? true,
        carArrivalTime: initialData?.carArrivalTime || '',
        loginAppTime: initialData?.loginAppTime || '',
        loginTelegramTime: initialData?.loginTelegramTime || '',
        id: initialData?.id || undefined
    }));

    const [showScanner, setShowScanner] = useState(false);
    const [scannedShiftsQueue, setScannedShiftsQueue] = useState<ShiftInput[]>([]);
    const [batchShifts, setBatchShifts] = useState<ShiftInput[]>([]);

    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // New Item Inputs
    const [newExpense, setNewExpense] = useState<{ category: ExpenseCategory, amount: string, origin: TransactionOrigin, description: string, date: string }>({
        category: 'FOOD',
        amount: '',
        origin: 'PERSONAL',
        description: '',
        date: initialData?.date || format(new Date(), 'yyyy-MM-dd')
    });
    const [newExtra, setNewExtra] = useState<{ category: ExtraIncomeCategory, amount: string, origin: TransactionOrigin, description: string, earning_date: string }>({
        category: 'UBER',
        amount: '',
        origin: 'THIRD_PARTY',
        description: '',
        earning_date: initialData?.date || format(new Date(), 'yyyy-MM-dd')
    });

    const [isExpensesOpen, setIsExpensesOpen] = useState(false);
    const [isExtrasOpen, setIsExtrasOpen] = useState(false);

    const isEditMode = !!initialData?.id;

    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setFormData(prev => ({ ...prev, ...initialData }));
            setScannedShiftsQueue([]);
        }
    }, [initialData]);

    // Preview Logic
    const preview = useMemo(() => {
        let rhinoEarnings = 0;
        let hours = 0;

        if (formData.type === 'WORK') {
            const result = calculateEarnings(
                formData.date,
                formData.startTime,
                formData.endTime,
                formData.isHoliday,
                formData.hasBilingual ? formData.bilingualStart : undefined,
                formData.hasBilingual ? formData.bilingualEnd : undefined,
                (userGeneration as 'OLD' | 'NEW') || 'OLD'
            );
            rhinoEarnings = result.totalEarnings;
            hours = result.totalHours;
        }

        const appTips = parseFloat(formData.appTips) || 0;
        const outsideTips = parseFloat(formData.tips) || 0;
        const totalTips = appTips + outsideTips;
        const totalRhino = rhinoEarnings + appTips; // Preview strictly separates appTips for display? No, user wants real revenue.

        // For Dashboard/Preview, we show what real revenue means
        const totalExtra = formData.extraEarnings.reduce((acc, curr) => acc + curr.amount, 0);
        const totalExpenses = formData.expenses.reduce((acc, curr) => acc + curr.amount, 0);

        const netBalance = (rhinoEarnings + totalTips + totalExtra) - totalExpenses;

        return { totalRhino: rhinoEarnings + appTips, totalTips, totalExtra, totalExpenses, netBalance, hours };
    }, [formData]);

    const handleChange = (field: keyof ShiftInput, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleTypeChange = (type: ShiftType) => {
        setFormData(prev => ({ ...prev, type }));
    };

    const showToast = (msg: string) => {
        setSuccessMessage(msg);
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    // --- Expenses Logic ---
    const addExpense = () => {
        if (!newExpense.amount) return;
        const amount = parseFloat(newExpense.amount);
        if (isNaN(amount) || amount <= 0) return;

        setFormData(prev => ({
            ...prev,
            expenses: [...prev.expenses, {
                id: Date.now().toString(),
                category: newExpense.category,
                origin: newExpense.origin,
                amount,
                description: newExpense.description,
                date: newExpense.date
            }]
        }));
        setNewExpense(prev => ({ ...prev, amount: '', description: '' }));
    };

    const removeExpense = (id: string) => {
        setFormData(prev => ({
            ...prev,
            expenses: prev.expenses.filter(e => e.id !== id)
        }));
    };

    // --- Extra Earnings Logic ---
    const addExtra = () => {
        if (!newExtra.amount) return;
        const amount = parseFloat(newExtra.amount);
        if (isNaN(amount) || amount <= 0) return;

        setFormData(prev => ({
            ...prev,
            extraEarnings: [...prev.extraEarnings, {
                id: Date.now().toString(),
                category: newExtra.category,
                origin: newExtra.origin,
                amount,
                description: newExtra.description,
                earning_date: newExtra.earning_date
            }]
        }));
        setNewExtra(prev => ({ ...prev, amount: '', description: '' }));
    };

    const removeExtra = (id: string) => {
        setFormData(prev => ({
            ...prev,
            extraEarnings: prev.extraEarnings.filter(e => e.id !== id)
        }));
    };

    const handleToggleScanner = () => {
        setShowScanner(!showScanner);
    };

    const handleScanComplete = async (data: ShiftInput[]) => {
        if (!data || data.length === 0) {
            setShowScanner(false);
            return;
        }

        setShowScanner(false);
        // Just populate the queue instead of saving immediately
        setScannedShiftsQueue(prev => [...prev, ...data]);
        showToast(`${data.length} turno(s) detectado(s).`);
    };

    const addToBatch = (shift: ShiftInput, index: number) => {
        setBatchShifts(prev => [...prev, { ...shift, appTips: '', tips: '', expenses: [] }]);
        setScannedShiftsQueue(prev => prev.filter((_, i) => i !== index));
    };

    const removeFromBatch = (index: number) => {
        setBatchShifts(prev => prev.filter((_, i) => i !== index));
    };

    const updateBatchItem = (index: number, field: keyof ShiftInput, value: any) => {
        const newBatch = [...batchShifts];
        newBatch[index] = { ...newBatch[index], [field]: value };
        setBatchShifts(newBatch);
    };

    const handleConfirmBatch = async () => {
        if (batchShifts.length === 0 || isSaving) return;

        setIsSaving(true);
        showToast(`Salvando ${batchShifts.length} turno(s)...`);

        try {
            const shiftsToSave: Shift[] = batchShifts.map(item => {
                const result = calculateEarnings(
                    item.date,
                    item.startTime,
                    item.endTime,
                    item.isHoliday,
                    item.hasBilingual ? item.bilingualStart : undefined,
                    item.hasBilingual ? item.bilingualEnd : undefined,
                    (userGeneration as 'OLD' | 'NEW') || 'OLD'
                );

                const batchExpenses: ExpenseItem[] = [];
                const tempGasto = parseFloat((item as any).tempExpenses) || 0;
                if (tempGasto > 0) {
                    batchExpenses.push({
                        id: generateId(),
                        category: 'OTHER',
                        origin: 'PERSONAL',
                        amount: tempGasto,
                        description: 'Gasto informado no lote'
                    });
                }

                return {
                    id: generateId(),
                    user_id: '', 
                    type: 'WORK',
                    noShow: false,
                    date: item.date,
                    startTime: item.startTime,
                    endTime: item.endTime,
                    isHoliday: item.isHoliday,
                    totalEarnings: result.totalEarnings,
                    totalHours: result.totalHours,
                    appTips: parseFloat(item.appTips) || 0,
                    tips: parseFloat(item.tips) || 0,
                    unpaidBreakMinutes: parseFloat(item.unpaidBreakMinutes) || 0,
                    breakdown: result.breakdown,
                    expenses: [...(item.expenses || []), ...batchExpenses],
                    extraEarnings: item.extraEarnings,
                    carArrivalTime: item.carArrivalTime || '',
                    loginAppTime: item.loginAppTime || '',
                    loginTelegramTime: item.loginTelegramTime || '',
                    vehiclePlate: item.vehiclePlate || '',
                };
            });

            await onSave(shiftsToSave, true);
            setBatchShifts([]);
            showToast(`✅ ${shiftsToSave.length} turno(s) salvos com sucesso!`);
        } catch (err) {
            console.error("Error saving batch shifts:", err);
            showToast('❌ Erro ao salvar turnos. Tente novamente.');
        } finally {
            setIsSaving(false);
        }
    };

    const batchTotal = batchShifts.reduce((acc, shift) => {
        const result = calculateEarnings(
            shift.date,
            shift.startTime,
            shift.endTime,
            shift.isHoliday,
            shift.hasBilingual ? shift.bilingualStart : undefined,
            shift.hasBilingual ? shift.bilingualEnd : undefined,
            (userGeneration as 'OLD' | 'NEW') || 'OLD'
        );
        const appTips = parseFloat(shift.appTips) || 0;
        const tips = parseFloat(shift.tips) || 0;
        const tempGasto = parseFloat((shift as any).tempExpenses) || 0;
        return acc + result.totalEarnings + appTips + tips - tempGasto;
    }, 0);


    // Robust UUID generator compatible with Postgres
    const generateId = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        // Fallback for environments without crypto.randomUUID
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSaving) return;

        // DUPLICATE CHECK
        if (!isEditMode && formData.type === 'WORK' && existingShifts.length > 0) {
            const isDuplicate = existingShifts.some(s => s.date === formData.date && s.type === 'WORK');
            if (isDuplicate) {
                const [y, m, d] = formData.date.split('-').map(Number);
                const dateObj = new Date(y, m - 1, d);
                const dateFormatted = format(dateObj, "dd 'de' MMMM", { locale: ptBR });

                const confirmed = window.confirm(
                    `⚠️ Atenção: Já existe um turno registrado em ${dateFormatted}.\n\n` +
                    `Inserir turnos duplicados pode causar imprecisões no cálculo financeiro.\n\n` +
                    `Deseja continuar mesmo assim?`
                );
                if (!confirmed) return;
            }
        }

        let result = { totalEarnings: 0, totalHours: 0, breakdown: { basePay: 0, nightPay: 0, bilingualPay: 0 } };

        if (formData.type === 'WORK') {
            result = calculateEarnings(
                formData.date,
                formData.startTime,
                formData.endTime,
                formData.isHoliday,
                formData.hasBilingual ? formData.bilingualStart : undefined,
                formData.hasBilingual ? formData.bilingualEnd : undefined,
                (userGeneration as 'OLD' | 'NEW') || 'OLD'
            );
        }

        const tipValue = parseFloat(formData.tips) || 0;

        // Use existing ID if editing, otherwise generate a new robust UUID
        const shiftId = formData.id || generateId();

        const newShift: Shift = {
            ...formData,
            id: shiftId,
            noShow: formData.type === 'NO_SHOW',
            // CORRECTION: totalEarnings is STRICTLY the calculated pay from hours.
            // Tips are stored separately in the 'tips' field.
            // The Dashboard will sum them (totalEarnings + tips).
            totalEarnings: result.totalEarnings,
            totalHours: result.totalHours,
            bilingualStart: formData.hasBilingual ? formData.bilingualStart : undefined,
            bilingualEnd: formData.hasBilingual ? formData.bilingualEnd : undefined,
            appTips: parseFloat(formData.appTips) || 0,
            tips: parseFloat(formData.tips) || 0,
            unpaidBreakMinutes: parseFloat(formData.unpaidBreakMinutes) || 0,
            breakdown: result.breakdown,
            expenses: formData.expenses,
            extraEarnings: formData.extraEarnings,
            vehiclePlate: formData.vehiclePlate,
            carReadyOnTime: formData.carReadyOnTime,
            carArrivalTime: formData.carArrivalTime,
            loginAppTime: formData.loginAppTime,
            loginTelegramTime: formData.loginTelegramTime
        };

        setIsSaving(true);
        try {
            // Process any items in the batch so they don't get lost if the user clicks the wrong save button
            const extraBatchesToSave: Shift[] = batchShifts.map(item => {
                const bResult = calculateEarnings(
                    item.date,
                    item.startTime,
                    item.endTime,
                    item.isHoliday,
                    item.hasBilingual ? item.bilingualStart : undefined,
                    item.hasBilingual ? item.bilingualEnd : undefined,
                    (userGeneration as 'OLD' | 'NEW') || 'OLD'
                );

                const batchExpenses: ExpenseItem[] = [];
                const tempGasto = parseFloat((item as any).tempExpenses) || 0;
                if (tempGasto > 0) {
                    batchExpenses.push({
                        id: generateId(),
                        category: 'OTHER',
                        origin: 'PERSONAL',
                        amount: tempGasto,
                        description: 'Gasto informado no lote'
                    });
                }

                return {
                    id: generateId(),
                    user_id: '', 
                    type: 'WORK',
                    noShow: false,
                    date: item.date,
                    startTime: item.startTime,
                    endTime: item.endTime,
                    isHoliday: item.isHoliday,
                    totalEarnings: bResult.totalEarnings,
                    totalHours: bResult.totalHours,
                    appTips: parseFloat(item.appTips) || 0,
                    tips: parseFloat(item.tips) || 0,
                    unpaidBreakMinutes: parseFloat(item.unpaidBreakMinutes) || 0,
                    breakdown: bResult.breakdown,
                    expenses: [...(item.expenses || []), ...batchExpenses],
                    extraEarnings: item.extraEarnings,
                    vehiclePlate: item.vehiclePlate || '',
                    carArrivalTime: item.carArrivalTime || '',
                    loginAppTime: item.loginAppTime || '',
                    loginTelegramTime: item.loginTelegramTime || ''
                };
            });

            // If we have items in the scanner queue, we stay here to process the next one
            const hasMoreItems = scannedShiftsQueue.length > 0;

            // Combine the main form shift + any batches
            const combinedShiftsToSave: Shift[] = [newShift, ...extraBatchesToSave];

            // Pass navigateToDashboard = !hasMoreItems. 
            // If queue has items, we do NOT navigate away.
            await onSave(combinedShiftsToSave, !hasMoreItems);
            
            if (extraBatchesToSave.length > 0) {
                setBatchShifts([]); // clear batch after saving
            }

            if (hasMoreItems) {
                showToast('Turno salvo! Carregando próximo da fila...');

                // Pop the next item from queue
                const nextItem = scannedShiftsQueue[0];
                const remainingQueue = scannedShiftsQueue.slice(1);

                setScannedShiftsQueue(remainingQueue);

                // Populate form with next item
                setFormData(prev => ({
                    ...prev,
                    type: 'WORK',
                    date: nextItem.date || prev.date,
                    startTime: nextItem.startTime || '06:00',
                    endTime: nextItem.endTime || '18:00',
                    isHoliday: nextItem.isHoliday || false,
                    hasBilingual: nextItem.hasBilingual || false,
                    bilingualStart: nextItem.bilingualStart || '19:00',
                    bilingualEnd: nextItem.bilingualEnd || '22:00',
                    tips: nextItem.tips || '',
                    expenses: nextItem.expenses || [],
                    extraEarnings: nextItem.extraEarnings || [],
                    vehiclePlate: nextItem.vehiclePlate || '',
                    carReadyOnTime: nextItem.carReadyOnTime ?? true,
                    carArrivalTime: nextItem.carArrivalTime || '',
                    loginAppTime: nextItem.loginAppTime || '',
                    loginTelegramTime: nextItem.loginTelegramTime || '',
                    id: undefined // Ensure new ID for next item
                }));
            } else {
                if (isEditMode) {
                    showToast('Turno atualizado com sucesso!');
                } else {
                    showToast('Turno salvo com sucesso!');
                    // If not scanning, App.tsx usually navigates to Dashboard. 
                    // We reset local form just in case.
                    setFormData({
                        type: 'WORK',
                        date: format(new Date(), 'yyyy-MM-dd'),
                        startTime: '06:00',
                        endTime: '18:00',
                        isHoliday: false,
                        hasBilingual: false,
                        bilingualStart: '19:00',
                        bilingualEnd: '22:00',
                        tips: '',
                        appTips: '',
                        unpaidBreakMinutes: '',
                        vehiclePlate: '',
                        carReadyOnTime: true,
                        carArrivalTime: '',
                        loginAppTime: '',
                        loginTelegramTime: '',
                        expenses: [],
                        extraEarnings: []
                    });
                }
            }
        } catch (error) {
            console.error(error);
            alert('Erro ao processar turno. Verifique se o banco de dados está online.');
        } finally {
            setIsSaving(false);
        }
    };

    const [savingGeneration, setSavingGeneration] = useState(false);

    const handleSelectGeneration = async (gen: 'OLD' | 'NEW') => {
        if (!userProfile?.id) return;
        setSavingGeneration(true);
        try {
            const isNewUser = !userProfile.generation_selected;

            const { data, error } = await supabase
                .rpc('select_generation_with_trial', { gen_choice: gen });

            if (error) throw error;
            
            if (onProfileUpdate) {
                onProfileUpdate();
            }
            
            if (gen === 'NEW' && !userProfile.is_premium && isNewUser && data?.trial_activated) {
                alert('🎉 Parabéns! Você ganhou 30 dias de acesso total grátis ao Driver Score Premium!');
            }
        } catch (error) {
            console.error('Error saving generation:', error);
            alert('Erro ao salvar sua escolha. Tente novamente.');
        } finally {
            setSavingGeneration(false);
        }
    };

    const requireGeneration = userProfile && !userProfile.generation_selected;

    if (requireGeneration) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-fade-in">
                <div className="bg-slate-800 p-6 sm:p-8 rounded-2xl max-w-lg w-full border border-orange-500/30 shadow-[0_0_40px_rgba(249,115,22,0.15)] h-auto max-h-[90vh] overflow-y-auto">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-orange-500/50">
                            <Briefcase className="w-8 h-8 text-orange-500" />
                        </div>
                        <h2 className="text-xl sm:text-2xl font-black text-white mb-3">Nova geração na Rhino? Não pague para aprender na marra! 📉</h2>
                        <div className="text-slate-400 text-xs sm:text-sm leading-relaxed text-left space-y-2">
                            <p>Sabemos que no começo a gente fica meio perdido com os custos e o cálculo da retenção. O Driver Score chegou para ser seu braço direito:</p>
                            <ul className="list-none space-y-1 pl-2">
                                <li>🔹 Escolha o seu modelo (Base fixa R$ 20/h ou padrão).</li>
                                <li>🔹 Veja seu ganho diário na tela do celular.</li>
                                <li>🔹 Controle tudo sem burocracia.</li>
                            </ul>
                            <p className="font-bold text-amber-400 mt-2">
                                Estamos dando 1 mês de acesso total para novos usuários. É a chance de organizar seu fluxo de trabalho de forma profissional, sem sustos no dia do pagamento.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4 mb-6">
                        <button 
                            onClick={() => handleSelectGeneration('OLD')}
                            disabled={savingGeneration}
                            className="w-full bg-slate-900 border border-slate-700 hover:border-emerald-500 hover:bg-emerald-500/10 p-4 sm:p-5 rounded-xl text-left transition-all group disabled:opacity-50"
                        >
                            <h3 className="text-emerald-400 font-bold text-lg mb-1 flex items-center gap-2">
                                Geração Antiga
                                <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                            </h3>
                            <p className="text-slate-400 text-[11px] sm:text-xs leading-relaxed">
                                Você recebe a <strong>taxa base (R$ 20,00)</strong> em dias úteis diurnos, e a <strong>taxa premium (R$ 26,00)</strong> no período noturno, finais de semana e feriados.
                            </p>
                        </button>

                        <button 
                            onClick={() => handleSelectGeneration('NEW')}
                            disabled={savingGeneration}
                            className="w-full bg-slate-900 border border-amber-500/50 hover:border-amber-400 hover:bg-amber-500/10 p-4 sm:p-5 rounded-xl text-left transition-all group disabled:opacity-50 relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 bg-amber-500 text-slate-900 text-[10px] font-black px-2 py-0.5 rounded-bl-lg rounded-tr-lg z-10">
                                +30 DIAS GRÁTIS
                            </div>
                            <h3 className="text-amber-400 font-bold text-lg mb-1 flex items-center gap-2 relative z-10">
                                Geração Nova
                                <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                            </h3>
                            <p className="text-slate-400 text-[11px] sm:text-xs leading-relaxed relative z-10">
                                Você recebe uma <strong>taxa fixa (R$ 20,00/h ou R$ 0,20 por centesimal)</strong> independentemente de ser noturno, fim de semana ou feriado.
                            </p>
                        </button>
                    </div>

                    <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800 text-center">
                        <p className="text-[10px] text-slate-500 italic">
                            Você sempre poderá alterar essa opção depois na aba de Configurações (Perfil).
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6 pb-20">

            {/* 1. Header & Type Selector */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 shadow-lg">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Calculator className="w-6 h-6 text-orange-500" />
                        {isEditMode ? 'Editar Turno' : 'Turnos Programados'}
                    </h2>
                    {!isEditMode && (
                        <button
                            type="button"
                            onClick={handleToggleScanner}
                            className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors border ${showScanner
                                ? 'bg-red-900/30 text-red-300 border-red-800 hover:bg-red-900/50'
                                : 'bg-slate-700 text-blue-300 border-slate-600 hover:bg-slate-600 hover:text-white'
                                }`}
                        >
                            {showScanner ? <X className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
                            {showScanner ? 'Fechar' : 'Escanear'}
                        </button>
                    )}
                </div>

                {scannedShiftsQueue.length > 0 && (
                    <div className="mb-6 animate-fade-in space-y-2">
                        <div className="bg-blue-900/10 border border-blue-500/30 p-4 rounded-xl">
                            <h4 className="text-white text-sm font-bold flex items-center gap-2 mb-4">
                                <CheckCircle2 className="w-5 h-5 text-blue-400" />
                                Resultados da Leitura ({scannedShiftsQueue.length})
                            </h4>
                            <div className="space-y-3">
                                {scannedShiftsQueue.map((shift, idx) => (
                                    <div key={idx} className="bg-slate-900/50 border border-slate-700 rounded-xl p-4 space-y-3">
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="bg-orange-600/10 p-2.5 rounded-xl">
                                                    <Calendar className="w-5 h-5 text-orange-500" />
                                                </div>
                                                <div>
                                                    <p className="text-white text-sm font-bold">{format(new Date(shift.date + 'T00:00:00'), "dd 'de' MMMM", { locale: ptBR })}</p>
                                                    <p className="text-slate-400 text-xs">Turno: {shift.startTime} – {shift.endTime}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => addToBatch(shift, idx)}
                                                    className="bg-orange-600 hover:bg-orange-500 text-white p-2 rounded-lg transition-all active:scale-90"
                                                    title="Adicionar ao subtotal"
                                                >
                                                    <PlusCircle className="w-5 h-5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setScannedShiftsQueue(prev => prev.filter((_, i) => i !== idx))}
                                                    className="text-slate-500 hover:text-red-400 p-2"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                        {/* Vehicle Plate */}
                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">🚗 Placa do Veículo</label>
                                            <input
                                                type="text"
                                                placeholder="Ex: TIW 7A01"
                                                value={shift.vehiclePlate || ''}
                                                onChange={(e) => {
                                                    const updated = [...scannedShiftsQueue];
                                                    updated[idx] = { ...updated[idx], vehiclePlate: e.target.value.toUpperCase() };
                                                    setScannedShiftsQueue(updated);
                                                }}
                                                className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2 text-white text-xs outline-none focus:border-emerald-500 uppercase"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {showScanner && (
                    <div className="mb-8 animate-fade-in bg-slate-900/50 p-6 rounded-xl border border-dashed border-slate-600">
                        <ImageUpload onDataParsed={handleScanComplete} />
                    </div>
                )}

                {/* TYPE SELECTOR BUTTONS */}
                <div className="grid grid-cols-3 gap-2 mb-6">
                    <button
                        type="button"
                        onClick={() => handleTypeChange('WORK')}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-all ${formData.type === 'WORK' ? 'bg-orange-600 text-white border-orange-500 shadow-md' : 'bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800'}`}
                    >
                        <Briefcase className="w-5 h-5 mb-1" />
                        <span className="text-xs font-bold">Trabalho</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleTypeChange('OFF')}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-all ${formData.type === 'OFF' ? 'bg-slate-600 text-white border-slate-500 shadow-md' : 'bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800'}`}
                    >
                        <Coffee className="w-5 h-5 mb-1" />
                        <span className="text-xs font-bold">Folga</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleTypeChange('NO_SHOW')}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-all ${formData.type === 'NO_SHOW' ? 'bg-red-600 text-white border-red-500 shadow-md' : 'bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800'}`}
                    >
                        <AlertTriangle className="w-5 h-5 mb-1" />
                        <span className="text-xs font-bold">Falta</span>
                    </button>
                </div>

                {/* Main Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className={formData.type === 'WORK' ? "grid grid-cols-1 md:grid-cols-2 gap-6" : ""}>
                        <div>
                            <label htmlFor="date" className="block text-sm font-medium text-slate-400 mb-2">Data</label>
                            <input
                                id="date"
                                name="date"
                                type="date"
                                required
                                value={formData.date}
                                onChange={(e) => handleChange('date', e.target.value)}
                                className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-orange-500 outline-none"
                            />
                        </div>

                        {formData.type === 'WORK' && (
                            <div className="flex items-end pb-3">
                                <label htmlFor="is_holiday" className="flex items-center space-x-3 cursor-pointer">
                                    <input
                                        id="is_holiday"
                                        name="is_holiday"
                                        type="checkbox"
                                        checked={formData.isHoliday}
                                        onChange={(e) => handleChange('isHoliday', e.target.checked)}
                                        className="w-5 h-5 rounded border-slate-600 text-orange-600 focus:ring-orange-500 bg-slate-900"
                                    />
                                    <span className="text-slate-300 font-medium">É Feriado?</span>
                                </label>
                            </div>
                        )}
                    </div>

                    {formData.type === 'WORK' && (
                        <>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="start_time" className="block text-sm font-medium text-slate-400 mb-2">Início Turno</label>
                                    <input
                                        id="start_time"
                                        name="start_time"
                                        type="time"
                                        required
                                        value={formData.startTime}
                                        onChange={(e) => handleChange('startTime', e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-orange-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="end_time" className="block text-sm font-medium text-slate-400 mb-2">Fim Turno</label>
                                    <input
                                        id="end_time"
                                        name="end_time"
                                        type="time"
                                        required
                                        value={formData.endTime}
                                        onChange={(e) => handleChange('endTime', e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-orange-500 outline-none"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="unpaid_break" className="block text-sm font-medium text-slate-400 mb-2 flex justify-between items-center group relative cursor-help">
                                        <span>Minutos Não Descansados</span>
                                        <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs text-slate-400 group-hover:bg-slate-700 transition-colors">?</div>
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-slate-800 border border-slate-700 rounded-lg shadow-xl text-xs text-slate-300 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 pointer-events-none">
                                            Os minutos salvos são convertidos para Hora Centesimal e multiplicados por R$ 0,20 cada unidade, sendo somados automaticamente ao saldo líquido do seu turno!
                                        </div>
                                    </label>
                                    <input
                                        id="unpaid_break"
                                        name="unpaid_break"
                                        type="number"
                                        min="0"
                                        value={formData.unpaidBreakMinutes}
                                        onChange={(e) => handleChange('unpaidBreakMinutes', e.target.value)}
                                        placeholder="0"
                                        className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-orange-500 outline-none font-mono"
                                    />
                                </div>
                                
                                <div>
                                    <label htmlFor="vehicle_plate" className="block text-sm font-medium text-slate-400 mb-2">Placa do Veículo *</label>
                                    <input
                                        id="vehicle_plate"
                                        name="vehicle_plate"
                                        type="text"
                                        required
                                        value={formData.vehiclePlate}
                                        onChange={(e) => {
                                            const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
                                            let formatted = value;
                                            if (value.length > 3) {
                                                formatted = value.slice(0, 3) + ' ' + value.slice(3);
                                            }
                                            handleChange('vehiclePlate', formatted);
                                        }}
                                        placeholder="ABC-1234"
                                        className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-orange-500 outline-none uppercase font-mono tracking-widest"
                                    />
                                </div>
                            </div>

                            <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="app_tips" className="block text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                                            <Briefcase className="w-4 h-4 text-orange-500" /> Gorj. App (Plific)
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3.5 text-slate-500 text-xs">R$</span>
                                            <input
                                                id="app_tips"
                                                name="app_tips"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={formData.appTips}
                                                onChange={(e) => handleChange('appTips', e.target.value)}
                                                placeholder="0.00"
                                                className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-8 pr-3 py-3 text-white focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="outside_tips" className="block text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                                            <Coins className="w-4 h-4 text-yellow-500" /> Gorj. Por Fora
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3.5 text-slate-500 text-xs">R$</span>
                                            <input
                                                id="outside_tips"
                                                name="outside_tips"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={formData.tips}
                                                onChange={(e) => handleChange('tips', e.target.value)}
                                                placeholder="0.00"
                                                className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-8 pr-3 py-3 text-white focus:ring-2 focus:ring-yellow-500 outline-none text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                                <label htmlFor="has_bilingual" className="flex items-center space-x-3 cursor-pointer mb-4">
                                    <input
                                        id="has_bilingual"
                                        type="checkbox"
                                        checked={formData.hasBilingual}
                                        onChange={(e) => handleChange('hasBilingual', e.target.checked)}
                                        className="w-5 h-5 rounded border-slate-600 text-blue-500 focus:ring-blue-500 bg-slate-900"
                                    />
                                    <span className="text-blue-400 font-bold">Evento Bilíngue (R$ 35/h)</span>
                                </label>
                                {formData.hasBilingual && (
                                    <div className="grid grid-cols-2 gap-4 animate-fade-in">
                                        <div>
                                            <label htmlFor="bilingual_start" className="block text-xs font-medium text-slate-500 mb-1">Início Evento</label>
                                            <input
                                                id="bilingual_start"
                                                name="bilingual_start"
                                                type="time"
                                                value={formData.bilingualStart}
                                                onChange={(e) => handleChange('bilingualStart', e.target.value)}
                                                className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm focus:border-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="bilingual_end" className="block text-xs font-medium text-slate-500 mb-1">Fim Evento</label>
                                            <input
                                                id="bilingual_end"
                                                name="bilingual_end"
                                                type="time"
                                                value={formData.bilingualEnd}
                                                onChange={(e) => handleChange('bilingualEnd', e.target.value)}
                                                className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm focus:border-blue-500 outline-none"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* --- SEÇÃO DE GASTOS --- */}
                    <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/50">
                        <button
                            type="button"
                            onClick={() => setIsExpensesOpen(!isExpensesOpen)}
                            className="w-full flex items-center justify-between text-white font-bold mb-1"
                        >
                            <span className="flex items-center gap-2">
                                <ShoppingCart className="w-4 h-4 text-red-500" /> Gastos
                            </span>
                            {isExpensesOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </button>
                        
                        {isExpensesOpen && (
                            <div className="mt-3 flex flex-col gap-3 animate-fade-in">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                                    {['FOOD', 'FUEL', 'TRANSPORT', 'ENTERTAINMENT', 'FIXED', 'OTHER'].map((cat) => (
                                        <button
                                            key={cat}
                                            type="button"
                                            onClick={() => setNewExpense({ ...newExpense, category: cat as any })}
                                            className={`p-2 rounded border ${newExpense.category === cat ? 'bg-red-900/50 border-red-500 text-red-200' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                        >
                                            {cat === 'FOOD' ? 'Alimentação' : cat === 'FUEL' ? 'Combustível' : cat === 'TRANSPORT' ? 'Transporte' : cat === 'ENTERTAINMENT' ? 'Lazer' : cat === 'FIXED' ? 'Fixo/Contas' : 'Outros'}
                                        </button>
                                    ))}
                                </div>
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                    <div className="col-span-2 flex flex-col gap-1">
                                        <label htmlFor="expense_origin" className="text-[10px] uppercase font-bold text-slate-500">Origem do Gasto</label>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            <button
                                                id="expense_origin_rhino"
                                                type="button"
                                                onClick={() => setNewExpense({ ...newExpense, origin: 'RHINO' })}
                                                className={`p-1.5 rounded border text-[10px] font-bold ${newExpense.origin === 'RHINO' ? 'bg-orange-600 border-orange-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                            >
                                                RHINO
                                            </button>
                                            <button
                                                id="expense_origin_apps"
                                                type="button"
                                                onClick={() => setNewExpense({ ...newExpense, origin: 'THIRD_PARTY' })}
                                                className={`p-1.5 rounded border text-[10px] font-bold ${newExpense.origin === 'THIRD_PARTY' ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                            >
                                                APPS
                                            </button>
                                            <button
                                                id="expense_origin_sales"
                                                type="button"
                                                onClick={() => setNewExpense({ ...newExpense, origin: 'SALES' })}
                                                className={`p-1.5 rounded border text-[10px] font-bold ${newExpense.origin === 'SALES' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                            >
                                                VENDAS
                                            </button>
                                            <button
                                                id="expense_origin_personal"
                                                type="button"
                                                onClick={() => setNewExpense({ ...newExpense, origin: 'PERSONAL' })}
                                                className={`p-1.5 rounded border text-[10px] font-bold ${newExpense.origin === 'PERSONAL' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                            >
                                                PESSOAL
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <label htmlFor="description" className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Descrição</label>
                                        <input
                                            id="description"
                                            name="description"
                                            type="text"
                                            placeholder="Ex: Almoço, Posto Graal..."
                                            value={newExpense.description}
                                            onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm outline-none focus:border-red-500 mb-3"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="date" className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Data Gasto</label>
                                        <input
                                            id="date"
                                            name="date"
                                            type="date"
                                            value={newExpense.date}
                                            onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm outline-none focus:border-red-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="amount" className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Valor do Gasto (R$)</label>
                                        <input
                                            id="amount"
                                            name="amount"
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={newExpense.amount}
                                            onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm outline-none focus:border-red-500"
                                        />
                                    </div>
                                    <div className="flex items-end">
                                        <button type="button" onClick={addExpense} className="bg-red-600 text-white px-4 h-[38px] rounded font-bold text-xl hover:bg-red-500 transition-colors w-full">+</button>
                                    </div>
                                </div>

                                {formData.expenses.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        {formData.expenses.map(item => (
                                            <div key={item.id} className="flex justify-between items-center bg-slate-800 p-2 rounded border border-slate-700 text-sm">
                                                <span className="text-slate-300">
                                                    {item.category === 'FOOD' && '🍔 Alimentação'}
                                                    {item.category === 'FUEL' && '⛽ Combustível'}
                                                    {item.category === 'TRANSPORT' && '🚌 Transporte'}
                                                    {item.category === 'ENTERTAINMENT' && '🎬 Lazer'}
                                                    {item.category === 'FIXED' && '💡 Fixo'}
                                                    {item.category === 'OTHER' && '📝 Outros'}
                                                    <span className={`ml-2 text-[8px] px-1 rounded font-black ${item.origin === 'RHINO' ? 'bg-orange-600/20 text-orange-500' : 'bg-blue-600/20 text-blue-500'}`}>{item.origin}</span>
                                                </span>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-red-400 font-bold">- R$ {item.amount.toFixed(2)}</span>
                                                    <button type="button" onClick={() => removeExpense(item.id)} className="text-slate-500 hover:text-white"><X className="w-3 h-3" /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* --- SEÇÃO DE RENDA EXTRA --- */}
                    <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/50">
                        <button
                            type="button"
                            onClick={() => setIsExtrasOpen(!isExtrasOpen)}
                            className="w-full flex items-center justify-between text-white font-bold mb-1"
                        >
                            <span className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-green-400" /> Renda Extra
                            </span>
                            {isExtrasOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </button>

                        {isExtrasOpen && (
                            <div className="mt-3 flex flex-col gap-3 animate-fade-in">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                                    {['UBER', '99', 'INDRIVE', 'TAXI', 'SALES', 'OTHER'].map((cat) => (
                                        <button
                                            key={cat}
                                            type="button"
                                            onClick={() => setNewExtra({ ...newExtra, category: cat as any })}
                                            className={`p-2 rounded border ${newExtra.category === cat ? 'bg-green-900/50 border-green-500 text-green-200' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                        >
                                            {cat === 'UBER' ? 'Uber' : cat === 'SALES' ? 'Vendas' : cat === 'OTHER' ? 'Outros' : cat}
                                        </button>
                                    ))}
                                </div>
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                    <div className="col-span-2 flex flex-col gap-1">
                                        <label htmlFor="extra_origin" className="text-[10px] uppercase font-bold text-slate-500">Origem da Renda</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                id="extra_origin_apps"
                                                type="button"
                                                onClick={() => setNewExtra({ ...newExtra, origin: 'THIRD_PARTY' })}
                                                className={`p-1.5 rounded border text-[10px] font-bold ${newExtra.origin === 'THIRD_PARTY' ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                            >
                                                APPS
                                            </button>
                                            <button
                                                id="extra_origin_sales"
                                                type="button"
                                                onClick={() => setNewExtra({ ...newExtra, origin: 'SALES' })}
                                                className={`p-1.5 rounded border text-[10px] font-bold ${newExtra.origin === 'SALES' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                            >
                                                VENDAS
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <label htmlFor="description" className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Descrição Ganho</label>
                                        <input
                                            id="description"
                                            name="description"
                                            type="text"
                                            placeholder="Ex: Gorjeta extra, Venda..."
                                            value={newExtra.description}
                                            onChange={(e) => setNewExtra({ ...newExtra, description: e.target.value })}
                                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm outline-none focus:border-green-500 mb-3"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="earning_date" className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Data Ganho</label>
                                        <input
                                            id="earning_date"
                                            name="earning_date"
                                            type="date"
                                            value={newExtra.earning_date}
                                            onChange={(e) => setNewExtra({ ...newExtra, earning_date: e.target.value })}
                                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm outline-none focus:border-green-500"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label htmlFor="amount" className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Valor da Renda (R$)</label>
                                        <input
                                            id="amount"
                                            name="amount"
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={newExtra.amount}
                                            onChange={(e) => setNewExtra({ ...newExtra, amount: e.target.value })}
                                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm outline-none focus:border-green-500"
                                        />
                                    </div>
                                    <div className="flex items-end">
                                        <button type="button" onClick={addExtra} className="bg-green-600 text-white px-4 h-[38px] rounded font-bold text-xl hover:bg-green-500 transition-colors w-full">+</button>
                                    </div>
                                </div>

                                {formData.extraEarnings.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        {formData.extraEarnings.map(item => (
                                            <div key={item.id} className="flex justify-between items-center bg-slate-800 p-2 rounded border border-slate-700 text-sm">
                                                <span className="text-slate-300 font-bold flex items-center gap-2">
                                                    <span>
                                                        {item.category === 'UBER' && 'Uber'}
                                                        {item.category === '99' && '99'}
                                                        {item.category === 'INDRIVE' && 'inDrive'}
                                                        {item.category === 'TAXI' && 'Táxi'}
                                                        {item.category === 'SALES' && 'Vendas'}
                                                        {item.category === 'OTHER' && 'Outros'}
                                                    </span>
                                                    <span className={`text-[8px] px-1 rounded font-black ${item.origin === 'SALES' ? 'bg-emerald-600/20 text-emerald-500' : 'bg-indigo-600/20 text-indigo-500'}`}>{item.origin}</span>
                                                </span>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-green-400 font-bold">+ R$ {item.amount.toFixed(2)}</span>
                                                    <button type="button" onClick={() => removeExtra(item.id)} className="text-slate-500 hover:text-white"><X className="w-3 h-3" /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {preview && (
                        <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-600 flex flex-col gap-2">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-400">Ganhos Rhino + Gorj. App</span>
                                <span className="text-slate-200">R$ {preview.totalRhino.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-400">Gorj. Por Fora</span>
                                <span className="text-yellow-500">+ R$ {preview.totalTips - (parseFloat(formData.appTips) || 0)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-400">Extras</span>
                                <span className="text-green-400">+ R$ {preview.totalExtra.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-400">Gastos Pessoais</span>
                                <span className="text-red-400">- R$ {preview.totalExpenses.toFixed(2)}</span>
                            </div>
                            <div className="h-[1px] bg-slate-600 my-1"></div>
                            <div className="flex justify-between items-center">
                                <span className="font-bold text-white">Saldo Líquido</span>
                                <span className={`text-xl font-bold ${preview.netBalance >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                                    R$ {preview.netBalance.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isSaving}
                        className={`w-full font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg ${isEditMode
                            ? 'bg-orange-600 hover:bg-orange-700 text-white'
                            : formData.type === 'OFF' ? 'bg-slate-600 hover:bg-slate-500 text-white'
                                : formData.type === 'NO_SHOW' ? 'bg-red-600 hover:bg-red-700 text-white'
                                    : 'bg-green-600 hover:bg-green-700 text-white'
                            } ${isSaving ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : (isEditMode ? <><Save className="w-5 h-5" /> Salvar Alterações</> : <><Save className="w-5 h-5" /> Salvar Turno</>)}
                    </button>
                </form>
            </div>

            {batchShifts.length > 0 && (
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 shadow-lg animate-fade-in">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
                        <Coins className="w-6 h-6 text-yellow-500" />
                        Subtotal / Lançamentos Agendados
                    </h3>
                    
                    <div className="space-y-4 mb-8">
                        {batchShifts.map((shift, idx) => (
                            <div key={idx} className="bg-slate-900/50 border border-slate-700 rounded-xl p-4 space-y-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-green-600/10 p-2 rounded-lg">
                                            <CalendarCheck className="w-5 h-5 text-green-500" />
                                        </div>
                                        <div>
                                            <p className="text-white text-sm font-bold">{format(new Date(shift.date + 'T00:00:00'), "dd 'de' MMMM", { locale: ptBR })}</p>
                                            <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">{shift.startTime} – {shift.endTime}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => removeFromBatch(idx)} className="text-slate-500 hover:text-red-400 p-1">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Gorj. App</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={shift.appTips}
                                            onChange={(e) => updateBatchItem(idx, 'appTips', e.target.value)}
                                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-xs outline-none focus:border-orange-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Gorj. Fora</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={shift.tips}
                                            onChange={(e) => updateBatchItem(idx, 'tips', e.target.value)}
                                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-xs outline-none focus:border-yellow-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Gastos (R$)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={(shift as any).tempExpenses || ''}
                                            onChange={(e) => updateBatchItem(idx, 'tempExpenses' as any, e.target.value)}
                                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-xs outline-none focus:border-red-500"
                                        />
                                    </div>
                                </div>
                                {/* Vehicle Plate */}
                                <div>
                                    <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">🚗 Placa do Veículo</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: TIW 7A01"
                                        value={shift.vehiclePlate || ''}
                                        onChange={(e) => updateBatchItem(idx, 'vehiclePlate', e.target.value.toUpperCase())}
                                        className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2 text-white text-xs outline-none focus:border-emerald-500 uppercase"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="bg-slate-900/80 p-4 rounded-xl border border-slate-700 mb-6">
                        <div className="flex justify-between items-center">
                            <span className="text-slate-400 text-sm font-bold">Resumo ({batchShifts.length} turnos)</span>
                            <span className="text-2xl font-black text-green-400">R$ {batchTotal.toFixed(2)}</span>
                        </div>
                    </div>

                    <button
                        onClick={handleConfirmBatch}
                        disabled={isSaving}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 disabled:opacity-50"
                    >
                        {isSaving ? <Loader2 className="w-6 h-6 animate-spin" /> : <><CheckCircle2 className="w-6 h-6" /> Confirmar e Salvar Tudo</>}
                    </button>
                </div>
            )}

            {successMessage && (
                <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-2 animate-bounce z-50 whitespace-nowrap">
                    <CheckCircle2 className="w-5 h-5" /><span>{successMessage}</span>
                </div>
            )}
        </div>
    );
};
